import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import HelpPage from './pages/HelpPage';
import DashboardPage from './pages/DashboardPage';
import OnlinePage from './pages/OnlinePage';
import KitchenScreenPage from './pages/KitchenScreenPage';
import WriteoffsPage from './pages/WriteoffsPage';
import KitchenPage from './pages/KitchenPage';
import CashPage from './pages/CashPage';
import ReceiptsPage from './pages/ReceiptsPage';
import EmployeesPage from './pages/EmployeesPage';
import KPIPage from './pages/KpiPage';
import ProfilePage from './pages/ProfilePage';
import MessagesPage from './pages/MessagesPage';
import CrmPage from './pages/CrmPage';
import UsersPage from './pages/UsersPage';
import PurchasesPage from './pages/PurchasesPage';
import PlatformPage from './pages/PlatformPage';
import BillingPage from './pages/BillingPage';
import SupportPage from './pages/SupportPage';
import ImpersonationBanner from './components/common/ImpersonationBanner';
import LoadingSpinner from './components/common/LoadingSpinner';
import NoAccess from './components/common/NoAccess';
import ModuleDisabled from './components/common/ModuleDisabled';
import { PATH_PAGES, homePath, moduleBlock } from './utils/pageAccess';
import './App.css';
import './index.css';


const ProtectedRoute = ({ children, path }) => {
  const { user, isAuthenticated, loading, isPlatformAdmin, canAccess } = useAuth();

  if (loading) {
    // Раньше здесь был голый <div>Загрузка...</div> в левом верхнем углу —
    // первое, что видел пользователь при каждом заходе.
    return (
      <div className="app-boot">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Суперадмин платформы не привязан к организации — обычные разделы
  // (дашборд, касса и т.д.) для него не работают, отправляем на /platform.
  if (isPlatformAdmin && window.location.pathname !== '/platform') {
    return <Navigate to="/platform" replace />;
  }

  // Страница, которую роль пользователя не открывает: объясняем, а не
  // показываем пустой экран с ошибками 403 из каждого запроса.
  const page = PATH_PAGES[path];
  if (!isPlatformAdmin && page && !canAccess(page)) {
    return <NoAccess home={homePath(user)} />;
  }

  // Раздел отключён платформой — для всех, включая администратора
  const block = !isPlatformAdmin && moduleBlock(user, page);
  if (block) {
    return <ModuleDisabled block={block} home={homePath(user)} />;
  }

  return children;
};

/* «/» ведёт на первую страницу, открытую роли, а не всегда на дашборд */
const HomeRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={homePath(user)} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/help" element={
        <ProtectedRoute path="/help">
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute path="/dashboard">
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/online" element={
        <ProtectedRoute path="/online">
          <OnlinePage />
        </ProtectedRoute>
      } />
      <Route path="/kitchen-screen" element={
        <ProtectedRoute path="/kitchen-screen">
          <KitchenScreenPage />
        </ProtectedRoute>
      } />
      <Route path="/writeoffs" element={
        <ProtectedRoute path="/writeoffs">
          <WriteoffsPage />
        </ProtectedRoute>
      } />
      <Route path="/kitchen" element={
        <ProtectedRoute path="/kitchen">
          <KitchenPage />
        </ProtectedRoute>
      } />
      <Route path="/cash" element={
        <ProtectedRoute path="/cash">
          <CashPage />
        </ProtectedRoute>
      } />
      <Route path="/receipts" element={
        <ProtectedRoute path="/receipts">
          <ReceiptsPage />
        </ProtectedRoute>
      } />
	  <Route path="/messages" element={
  <ProtectedRoute path="/messages">
    <MessagesPage />
  </ProtectedRoute>
} />
      <Route path="/employees" element={
        <ProtectedRoute path="/employees">
          <EmployeesPage />
        </ProtectedRoute>
      } />
      <Route path="/kpi" element={
        <ProtectedRoute path="/kpi">
          <KPIPage />
        </ProtectedRoute>
      } />
      {/* «Лояльность» и «База гостей» объединены в CRM — старые ссылки просто ведут туда же */}
      <Route path="/loyalty" element={<Navigate to="/crm" replace />} />
      <Route path="/guests" element={<Navigate to="/crm" replace />} />
      <Route path="/crm" element={
        <ProtectedRoute path="/crm">
          <CrmPage />
        </ProtectedRoute>
      } />
      <Route path="/purchases" element={
        <ProtectedRoute path="/purchases">
          <PurchasesPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute path="/users">
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/platform" element={
        <ProtectedRoute path="/platform">
          <PlatformPage />
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute path="/billing">
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute path="/profile">
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute path="/support">
          <SupportPage />
        </ProtectedRoute>
      } />
      <Route path="/" element={<HomeRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        {/* Над роутами: полоса режима просмотра должна быть видна на любом экране */}
        <ImpersonationBanner />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
